import { useEffect } from 'react'
import { z } from 'zod'
import { BaseActionDefinitionEnhanced } from '~/prix/actionDefinition'
import useActionFunction from './actionFunction'

export default function useAction<
  ActionDefinition extends BaseActionDefinitionEnhanced<any, any>,
>(
  actionDefinition: ActionDefinition,
  input: ActionDefinition extends BaseActionDefinitionEnhanced<infer I, any> ? z.infer<I> : any,
  deps: React.DependencyList = input && typeof input === 'object' ? Object.values(input) : [input],
  {
    autoLoad = true,
    staleWhileRevalidate = false,
    sourceKey = 'default',
    onError,
  }: {
    autoLoad?: boolean
    staleWhileRevalidate?: boolean
    sourceKey?: string
    onError?: (error: Error) => void
  } = {},
) {
  const { callAction, result, error, isLoading } = useActionFunction(actionDefinition, {
    sourceKey,
    staleWhileRevalidate,
    initialIsLoading: autoLoad,
  })

  useEffect(() => {
    if (!autoLoad) {
      return
    }
    const { abort, resultPromise } = callAction(input)
    resultPromise.catch(error => {
      console.error(error)
      if (onError) {
        onError(error)
      }
    })
    return () => {
      abort()
    }
  }, [callAction, autoLoad, ...deps])

  return {
    result,
    error,
    isLoading,
    callAction,
  } as {
    result: ActionDefinition extends BaseActionDefinitionEnhanced<any, infer O> ? z.infer<O> | undefined : any
    error: Error | null
    isLoading: boolean
    callAction: (input: ActionDefinition extends BaseActionDefinitionEnhanced<infer I, any> ? z.infer<I> : any) => {
      abort: () => void
      resultPromise: Promise<ActionDefinition extends BaseActionDefinitionEnhanced<any, infer O> ? z.infer<O> : any>
    }
  }
}
